import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import TenantCombobox from '../ui/TenantCombobox';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { track, trackFirstTime } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecordPaymentModal = ({ isOpen, onClose, onSuccess, token, presetTenant = null }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTenantInfo, setSelectedTenantInfo] = useState(null); // Store full tenant info
  const [roomInfo, setRoomInfo] = useState(null); // Store room info for payment mode detection
  const [formData, setFormData] = useState({
    tenant_id: '',
    property_id: '',
    room_id: '',
    amount: '',
    payment_type: 'rent',
    payment_method: 'upi',
    payment_date: new Date().toISOString().split('T')[0],
    month: new Date().toISOString().slice(0, 7), // YYYY-MM
    transaction_id: '',
    notes: ''
  });

  const fetchTenants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tenants?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tenantList = response.data || [];

      // Batch: fetch rooms ONCE per unique property, not per tenant
      const propertyIds = [...new Set(tenantList.map(t => t.property_id).filter(Boolean))];
      const roomsByProperty = {};
      await Promise.all(propertyIds.map(async (pid) => {
        try {
          const r = await axios.get(`${API}/properties/${pid}/rooms`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          roomsByProperty[pid] = r.data || [];
        } catch {
          roomsByProperty[pid] = [];
        }
      }));

      const tenantsWithRooms = tenantList.map((tenant) => {
        const rooms = roomsByProperty[tenant.property_id] || [];
        const room = rooms.find(r => r.id === tenant.room_id);
        return { ...tenant, room_number: room?.room_number || 'N/A' };
      });

      setTenants(tenantsWithRooms);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchTenants();
    }
  }, [isOpen, fetchTenants]);

  // If a preset tenant is passed (e.g. opened from Tenant Profile), preload form
  useEffect(() => {
    if (!isOpen || !presetTenant) return;
    setSelectedTenantInfo(presetTenant);
    setFormData(f => ({
      ...f,
      tenant_id: presetTenant.id,
      property_id: presetTenant.property_id || '',
      room_id: presetTenant.room_id || '',
      amount: presetTenant.rent_amount || '',
    }));
    // Pull room info for display, best-effort
    if (presetTenant.room_id && presetTenant.property_id) {
      axios.get(`${API}/properties/${presetTenant.property_id}/rooms`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => {
        const room = (r.data || []).find(x => x.id === presetTenant.room_id);
        if (room) setRoomInfo(room);
      }).catch(() => {});
    }
  }, [isOpen, presetTenant, token]);

  const handleTenantChange = async (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    setSelectedTenantInfo(tenant);
    
    // Fetch room information to determine payment mode
    if (tenant && tenant.room_id && tenant.property_id) {
      try {
        const roomRes = await axios.get(`${API}/properties/${tenant.property_id}/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const room = roomRes.data.find(r => r.id === tenant.room_id);
        setRoomInfo(room);
        
        setFormData({
          ...formData,
          tenant_id: tenantId,
          property_id: tenant.property_id,
          room_id: tenant.room_id,
          amount: tenant.rent_amount || ''
        });
      } catch (error) {
        console.error('Error fetching room info:', error);
        setFormData({
          ...formData,
          tenant_id: tenantId,
          property_id: tenant.property_id || '',
          amount: tenant.rent_amount || ''
        });
      }
    } else {
      setFormData({
        ...formData,
        tenant_id: tenantId,
        property_id: tenant?.property_id || '',
        amount: tenant?.rent_amount || ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/money/payments`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Broadcast: every open page that cares about money refreshes itself
      emit(EVT.PAYMENT, { action: 'created', tenant_id: formData.tenant_id, amount: formData.amount });
      emit(EVT.TENANT,  { action: 'payment_status_synced', tenant_id: formData.tenant_id });
      track('payment_recorded', {
        payment_type: formData.payment_type,
        payment_method: formData.payment_method,
        amount: Number(formData.amount) || 0,
      });
      trackFirstTime('first_payment_recorded', { amount: Number(formData.amount) || 0 });

      toast({
        title: "Payment Recorded!",
        description: `₹${formData.amount} payment recorded successfully`
      });

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to record payment",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  function resetForm() {
    setSelectedTenantInfo(null); // Reset selected tenant info
    setRoomInfo(null); // Reset room info
    setFormData({
      tenant_id: '',
      property_id: '',
      room_id: '',
      amount: '',
      payment_type: 'rent',
      payment_method: 'upi',
      payment_date: new Date().toISOString().split('T')[0],
      month: new Date().toISOString().slice(0, 7),
      transaction_id: '',
      notes: ''
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {!presetTenant && (
              <div className="col-span-2">
                <Label>Select Tenant *</Label>
                <TenantCombobox
                  tenants={tenants}
                  value={formData.tenant_id}
                  onChange={(id) => handleTenantChange(id)}
                  required
                  testId="payment-tenant-combobox"
                />
              </div>
            )}

            {/* Show selected tenant info */}
            {selectedTenantInfo && (
              <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Tenant:</span>
                    <span className="ml-2 font-medium text-gray-900">{selectedTenantInfo.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Room:</span>
                    <span className="ml-2 font-medium text-gray-900">🚪 {selectedTenantInfo.room_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Rent:</span>
                    <span className="ml-2 font-medium text-gray-900">₹{selectedTenantInfo.rent_amount?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Deposit:</span>
                    <span className="ml-2 font-medium text-gray-900">₹{selectedTenantInfo.deposit_amount?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Show room payment mode info */}
            {roomInfo && roomInfo.tenants && (
              <div className="col-span-2">
                {roomInfo.total_beds === 1 ? (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm font-semibold text-purple-900 mb-1">
                      🏠 Room-Level Payment (Shared Room)
                    </p>
                    <p className="text-xs text-purple-700">
                      Room {roomInfo.room_number} is shared by {roomInfo.tenants.length} tenant(s).
                      This payment will mark the room rent as paid for all tenants.
                    </p>
                    {roomInfo.tenants.length > 1 && (
                      <div className="mt-2 text-xs text-purple-600">
                        <span className="font-medium">Tenants: </span>
                        {roomInfo.tenants.map(t => t.name).join(', ')}
                      </div>
                    )}
                  </div>
                ) : roomInfo.tenants.length > 1 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      👤 Individual Payment
                    </p>
                    <p className="text-xs text-green-700">
                      This payment is for {selectedTenantInfo?.name} only.
                      Other tenants in Room {roomInfo.room_number} pay separately.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="5000"
                required
              />
            </div>

            <div>
              <Label>Payment Type *</Label>
              <select
                value={formData.payment_type}
                onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="rent">Rent</option>
                <option value="deposit">Deposit</option>
                <option value="electricity">Electricity</option>
                <option value="water">Water</option>
                <option value="maintenance">Maintenance</option>
                <option value="food">Food</option>
                <option value="late_fine">Late Fine</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label>Payment Method *</Label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>

            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Month (YYYY-MM) *</Label>
              <Input
                type="month"
                value={formData.month}
                onChange={(e) => setFormData({...formData, month: e.target.value})}
                required
              />
            </div>

            <div className="col-span-2">
              <Label>Transaction ID</Label>
              <Input
                value={formData.transaction_id}
                onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                placeholder="UPI/Bank transaction reference"
              />
            </div>

            <div className="col-span-2">
              <Label>Notes</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Any additional notes..."
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" disabled={loading}>
              {loading ? 'Recording...' : 'Record Payment'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentModal;
